import { Injectable } from '@angular/core';
import { ListingsRepository } from '../../../domain/repositories/listings.repository';
import { Result } from '../../../domain/models/http-response.model';
import { HttpClientAdapter } from '../../../domain/adapters/http.adapter';
import { lastValueFrom } from 'rxjs';
import {
  ListingAttributes,
  ListingModel,
  ListingQueryParams,
  ListingCollectionModel,
} from '../../../domain/models/listing.model';
import {
  GetListingsResponse,
  GetSingleListingResponse,
  ListingInquiryResponse,
} from './listings.responses';
import { ListingResponseMapper } from './mappers/listing-response.mapper';
import {
  ListingInquiryDto,
  ListingDto,
  ListingImageDto,
} from '../../../domain/dtos/listing.dto';
import { objectToFormData } from '../../../lib/object-to-form-data';
import { SingleListingResponseMapper } from './mappers/single-listing-response.mapper';
import { ListingFiltersParserUtility } from '../../utilities/listing-filters-parser/listing-filters-parser.utility';

@Injectable()
export class ListingsRepositoryImpl implements ListingsRepository {
  constructor(
    private httpClient: HttpClientAdapter,
    private listingFiltersParserUtility: ListingFiltersParserUtility
  ) {}

  async getListings(
    params: ListingQueryParams
  ): Promise<Result<ListingCollectionModel, Error>> {
    // const queryParams = this._buildQueryParams(params);
    const queryParams = this.listingFiltersParserUtility.toQuery(params);

    const result = await lastValueFrom(
      this.httpClient.request<GetListingsResponse>({
        method: 'GET',
        url: `/listings?${queryParams}`,
      })
    );

    if (result.isErr()) return result;

    const responseMapper = new ListingResponseMapper();
    const mappedResult = responseMapper.mapFrom(result.unwrap());

    return new Result(mappedResult, null);
  }

  async getMyListings(
    params: ListingQueryParams
  ): Promise<Result<ListingCollectionModel, Error>> {
    const queryParams = this.listingFiltersParserUtility.toQuery(params);

    const result = await lastValueFrom(
      this.httpClient.request<GetListingsResponse>({
        method: 'GET',
        url: `/listings/user/me?${queryParams}`,
      })
    );

    if (result.isErr()) return result;

    const responseMapper = new ListingResponseMapper();
    const mappedResult = responseMapper.mapFrom(result.unwrap());

    return new Result(mappedResult, null);
  }

  async createListing(
    newListing: ListingDto
  ): Promise<Result<ListingModel, Error>> {
    const result = await lastValueFrom(
      this.httpClient.request<GetSingleListingResponse>({
        method: 'POST',
        url: `/listings`,
        headers: {
          // 'Content-Type': 'multipart/form-data',
          Accept: 'application/json',
        },
        body: newListing,
      })
    );

    if (result.isErr()) return result;

    const responseMapper = new SingleListingResponseMapper();
    const listing = responseMapper.mapFrom(result.unwrap());

    return new Result<ListingModel, Error>(listing, null);
  }

  async updateListing(
    slug: string,
    listingData: ListingDto
  ): Promise<Result<unknown, Error>> {
    return await lastValueFrom(
      this.httpClient.request<GetListingsResponse>({
        method: 'POST',
        url: `/listings/${slug}`,
        headers: {
          Accept: 'application/json',
        },
        body: listingData,
      })
    );
  }

  async updateListingImages(
    idOrSlug: string,
    images: ListingImageDto[]
  ): Promise<Result<unknown, Error>> {
    const formData = objectToFormData({ images });
    return await lastValueFrom(
      this.httpClient.request<GetListingsResponse>({
        method: 'POST',
        url: `/listings/${idOrSlug}/images`,
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      })
    );
  }

  async getListingBySlug(slug: string): Promise<Result<ListingModel, Error>> {
    const result = await lastValueFrom(
      this.httpClient.request<GetSingleListingResponse>({
        method: 'GET',
        url: `/listings/${slug}`,
      })
    );

    if (result.isErr()) {
      return result;
    }
    const mapper = new SingleListingResponseMapper();
    const mappedListing = mapper.mapFrom(result.unwrap());

    return new Result<ListingModel, Error>(mappedListing, null);
  }

  async getListingById(id: string): Promise<Result<ListingModel, Error>> {
    const result = await lastValueFrom(
      this.httpClient.request<GetSingleListingResponse>({
        method: 'GET',
        url: `/listings/${id}`,
      })
    );

    if (result.isErr()) {
      return result;
    }
    const mapper = new SingleListingResponseMapper();
    const mappedListing = mapper.mapFrom(result.unwrap());

    return new Result<ListingModel, Error>(mappedListing, null);
  }

  async listingInquiry(
    data: ListingInquiryDto
  ): Promise<Result<ListingInquiryResponse, Error>> {
    return lastValueFrom(
      this.httpClient.request<ListingInquiryResponse>({
        method: 'POST',
        headers: {
          accept: 'application/json',
        },
        url: '/listings/inquiry',
        body: {
          ...data,
        },
      })
    );
  }
  async deleteListing(id: string): Promise<Result<unknown, Error>> {
    return await lastValueFrom(
      this.httpClient.request<unknown>({
        method: 'DELETE',
        url: `/listings/${id}`,
      })
    );
  }
}
